import { useEffect, useRef, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import {
  PaymentRequestItem,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  Stripe,
} from "@stripe/stripe-js";

import { PaymentMethod, ISetupIntent, StripeCardDeclineCode, WalletType } from "common/types";
import { useFetchMutation, useFetchQuery } from "common/queries/hooks";

import { useInvalidateClient } from "client/accounts/hooks/invalidate";

import { PaymentMethodCreate } from "../types";
import { keys, urls } from "./constants";

const noop = () => null;

export const useCreatePaymentMethodMutation = ({
  onSuccess = noop,
  onError = noop,
}: { onSuccess?: (response: PaymentMethod) => void; onError?: () => void } = {}) => {
  const invalidateClient = useInvalidateClient();
  return useFetchMutation<PaymentMethodCreate, PaymentMethod>({
    fetch: (body) => ({ method: "POST", url: urls.paymentMethods(), body }),
    config: {
      onSuccess: (method) => {
        invalidateClient();
        if (method) {
          onSuccess(method);
        }
      },
      onError,
    },
  });
};

export const useDeletePaymentMethodMutation = (
  id: number,
  { onSuccess = noop }: { onSuccess?: () => void } = {},
) => {
  const invalidateClient = useInvalidateClient();
  return useFetchMutation<void, void>({
    fetch: () => ({ method: "DELETE", url: urls.paymentMethod(id) }),
    config: {
      onSuccess: () => {
        invalidateClient();
        onSuccess();
      },
    },
  });
};

export const useSetupIntentMutation = () => {
  return useFetchMutation<void, ISetupIntent>({
    fetch: () => ({ method: "POST", url: urls.setupIntents() }),
  });
};

export const useStripeCardDeclineCodesQuery = () => {
  return useFetchQuery<StripeCardDeclineCode[]>(keys.stripeCardDeclineCodes(), {
    fetch: { method: "GET", url: urls.stripeCardDeclineCodes() },
  });
};

type WalletPaymentMethodRequestOpts = {
  label: string;
  amount: number;
  paymentItems: PaymentRequestItem[];
  onCreate: (id: string) => Promise<void>;
};

type WalletPaymentMethodRequestData = {
  request: PaymentRequest;
  supportedWallets: Set<WalletType>;
};

type WalletPaymentMethodRequestResult = {
  request: PaymentRequest | null;
  supportedWallets: Set<WalletType>;
  isLoading: boolean;
};

const walletTypeToCanMakePaymentKey: Partial<Record<WalletType, string>> = {
  [WalletType.APPLE]: "applePay",
  [WalletType.GOOGLE]: "googlePay",
};

const buildPaymentRequest = (
  stripe: Stripe,
  { label, amount, paymentItems }: WalletPaymentMethodRequestOpts,
) => {
  return stripe.paymentRequest({
    country: "US",
    currency: "usd",
    total: { label, amount },
    displayItems: paymentItems,
    requestPayerName: true,
    requestPayerEmail: true,
  });
};

export const useWalletPaymentMethodRequest = ({
  label,
  amount,
  paymentItems,
  onCreate,
}: WalletPaymentMethodRequestOpts): WalletPaymentMethodRequestResult => {
  const stripe = useStripe();

  const [data, setData] = useState<WalletPaymentMethodRequestData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const paymentRequestRef = useRef<PaymentRequest>(null);

  useEffect(() => {
    if (!stripe) {
      setIsLoading(false);
      return undefined;
    }

    if (!paymentRequestRef.current) {
      paymentRequestRef.current = buildPaymentRequest(stripe, {
        label,
        amount,
        paymentItems,
        onCreate,
      });
    } else {
      paymentRequestRef.current.update({
        total: { label, amount },
        displayItems: paymentItems,
      });
    }

    const pr = paymentRequestRef.current;

    // Check the availability of the Payment Request API.
    pr.canMakePayment().then((result) => {
      if (result) {
        setData({
          request: pr,
          supportedWallets: new Set(
            Object.values(WalletType).filter(
              (w) => walletTypeToCanMakePaymentKey[w] && result[walletTypeToCanMakePaymentKey[w]],
            ),
          ),
        });
      }
      setIsLoading(false);
    });

    const createHandler = async (event: PaymentRequestPaymentMethodEvent) => {
      try {
        await onCreate(event.paymentMethod.id);
        event.complete("success");
      } catch {
        event.complete("fail");
      }
    };

    pr.on("paymentmethod", createHandler);

    return () => {
      pr.off("paymentmethod", createHandler);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, label, JSON.stringify(paymentItems), stripe]);

  return {
    request: data?.request ?? null,
    supportedWallets: data?.supportedWallets ?? new Set(),
    isLoading,
  };
};
